import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { PageHeader, Loading, Field, ImageUpload } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Plus, UserCog, KeyRound, Power, Tag, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { isAdmin, user: currentUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [live, setLive] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userOpen, setUserOpen] = useState(false);
  const [uform, setUform] = useState({ name: "", email: "", password: "", role: "staff", can_delete: false });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({ id: null, name: "", description: "", image: null, active: true });
  const [catDelTarget, setCatDelTarget] = useState(null);

  const loadCats = useCallback(async () => {
    try { const c = await api.get("/categories"); setCategories(c.data); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/settings"); setSettings(s.data);
      if (isAdmin) { const u = await api.get("/users"); setUsers(u.data); const a = await api.get("/audit", { params: { limit: 50 } }); setAudit(a.data); await loadCats(); }
    } finally { setLoading(false); }
  }, [isAdmin, loadCats]);
  useEffect(() => { load(); }, [load]);

  const fetchLive = async () => { try { const r = await api.get("/exchange-rate/live"); setLive(r.data); toast.success(`Kurs: ${r.data.rate}`); } catch { toast.error("Gagal"); } };
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const saveSettings = async () => { try { await api.put("/settings", settings); toast.success("Pengaturan disimpan"); } catch { toast.error("Gagal"); } };
  const createUser = async () => {
    if (!uform.name || !uform.email || !uform.password) return toast.error("Lengkapi data");
    try { await api.post("/users", uform); toast.success("Pengguna ditambahkan"); setUserOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const doResetPassword = async () => {
    if (!resetPwd || resetPwd.length < 6) return toast.error("Password minimal 6 karakter");
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPwd });
      toast.success(`Password ${resetTarget.name} direset`);
      setResetTarget(null); setResetPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal reset"); }
  };

  const toggleActive = async (u) => {
    try {
      if (u.active) {
        await api.delete(`/users/${u.id}`);
        toast.success(`${u.name} dinonaktifkan`);
      } else {
        await api.put(`/users/${u.id}`, { active: true });
        toast.success(`${u.name} diaktifkan`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openNewCat = () => { setCatForm({ id: null, name: "", description: "", image: null, active: true }); setCatOpen(true); };
  const openEditCat = (c) => { setCatForm({ id: c.id, name: c.name, description: c.description || "", image: c.image || null, active: c.active !== false }); setCatOpen(true); };
  const saveCategory = async () => {
    if (!catForm.name?.trim()) return toast.error("Nama kategori wajib diisi");
    const payload = { name: catForm.name.trim(), description: catForm.description || "", image: catForm.image || "", active: !!catForm.active };
    try {
      if (catForm.id) { await api.put(`/categories/${catForm.id}`, payload); toast.success("Kategori diperbarui"); }
      else { await api.post("/categories", payload); toast.success("Kategori ditambahkan"); }
      setCatOpen(false); loadCats();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };
  const toggleCatActive = async (c) => {
    try { await api.put(`/categories/${c.id}`, { active: !(c.active !== false) }); loadCats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deleteCategory = async () => {
    try { const r = await api.delete(`/categories/${catDelTarget.id}`); toast.success(r.data?.message || "Kategori dihapus"); setCatDelTarget(null); loadCats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };
  const moveCat = async (index, dir) => {
    const arr = [...categories];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setCategories(arr);
    try { await api.post("/categories/reorder", { ordered_ids: arr.map((c) => c.id) }); }
    catch { loadCats(); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader title="Pengaturan" subtitle="Konfigurasi bisnis, kurs, dan staff" />
      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">Umum</TabsTrigger>
          <TabsTrigger value="rate" data-testid="tab-rate">Kurs</TabsTrigger>
          {isAdmin && <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>}
          {isAdmin && <TabsTrigger value="categories" data-testid="tab-categories">Kategori</TabsTrigger>}
          {isAdmin && <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>}
        </TabsList>

        <TabsContent value="general">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm max-w-lg space-y-4">
            <Field label="Nama Bisnis"><Input value={settings.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="settings-business-name" /></Field>
            <Field label="Nomor WhatsApp" hint="Format 628... untuk tombol pesan katalog"><Input value={settings.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} data-testid="settings-whatsapp" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Margin Default (%)"><Input value={settings.default_margin_percent || ""} onChange={(e) => set("default_margin_percent", e.target.value)} /></Field>
              <Field label="Jasa Titip Default"><Input value={settings.default_jasa_titip_value || ""} onChange={(e) => set("default_jasa_titip_value", e.target.value)} /></Field>
            </div>
            {isAdmin && <Button onClick={saveSettings} data-testid="settings-save">Simpan</Button>}
          </div>
        </TabsContent>

        <TabsContent value="rate">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm max-w-lg">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-muted-foreground">Kurs Live AUD→IDR</p>
                <p className="font-heading text-3xl font-semibold" data-testid="exchange-rate-value">{live ? Number(live.rate).toLocaleString("id-ID") : "—"}</p>
                {live && <p className="text-xs text-muted-foreground" data-testid="exchange-rate-updated-at">{live.source} · {formatDateTime(live.updated_at)} {live.live ? "(live)" : "(cache)"}</p>}
              </div>
              <Button onClick={fetchLive} data-testid="fetch-live-rate"><RefreshCw className="h-4 w-4 mr-1.5" />Ambil Live</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">Kurs per-trip diatur di masing-masing trip. Kurs order dikunci saat order dibuat.</p>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="staff">
            <div className="flex justify-end mb-3"><Button onClick={() => { setUform({ name: "", email: "", password: "", role: "staff", can_delete: false }); setUserOpen(true); }} data-testid="add-staff-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Pengguna</Button></div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-3.5 border-b border-border last:border-0" data-testid="user-row">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center"><UserCog className="h-4 w-4" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{u.name}</p><p className="text-xs text-muted-foreground truncate">{u.email}</p></div>
                  <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">{u.role}</span>
                  <span className={`text-xs ${u.active ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                  <Button size="sm" variant="outline" onClick={() => { setResetTarget(u); setResetPwd(""); }} data-testid="reset-password-button" title="Reset Password"><KeyRound className="h-3.5 w-3.5" /></Button>
                  {u.id !== currentUser?.id && (
                    <Button size="sm" variant="outline" className={u.active ? "text-destructive" : "text-[hsl(var(--success))]"} onClick={() => toggleActive(u)} data-testid="toggle-active-button" title={u.active ? "Nonaktifkan" : "Aktifkan"}><Power className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="categories">
            <div className="flex justify-end mb-3"><Button onClick={openNewCat} data-testid="add-category-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Kategori</Button></div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {categories.length === 0 ? <p className="text-sm text-muted-foreground p-6 text-center">Belum ada kategori</p> :
                categories.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-3 p-3.5 border-b border-border last:border-0" data-testid="category-row">
                    <div className="flex flex-col">
                      <button disabled={i === 0} onClick={() => moveCat(i, -1)} className="text-muted-foreground disabled:opacity-30 hover:text-foreground transition-colors" data-testid="category-move-up" title="Naik"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button disabled={i === categories.length - 1} onClick={() => moveCat(i, 1)} className="text-muted-foreground disabled:opacity-30 hover:text-foreground transition-colors" data-testid="category-move-down" title="Turun"><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                      {c.image ? <img src={c.image} alt={c.name} className="h-full w-full object-cover" /> : <Tag className="h-4 w-4 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      {c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-secondary shrink-0">{c.product_count} produk</span>
                    <span className={`text-xs shrink-0 ${c.active !== false ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{c.active !== false ? "Aktif" : "Nonaktif"}</span>
                    <Switch checked={c.active !== false} onCheckedChange={() => toggleCatActive(c)} data-testid="category-active-toggle" />
                    <Button size="sm" variant="outline" onClick={() => openEditCat(c)} data-testid="category-edit-button"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => setCatDelTarget(c)} data-testid="category-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Gunakan panah untuk mengatur urutan tampil. Kategori yang masih dipakai produk akan dinonaktifkan (bukan dihapus permanen).</p>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="audit">
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {audit.length === 0 ? <p className="text-sm text-muted-foreground p-6 text-center">Belum ada aktivitas</p> :
                audit.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 p-3 border-b border-border last:border-0 text-sm">
                    <span className="px-2 py-0.5 rounded bg-secondary text-xs">{a.action}</span>
                    <span className="flex-1">{a.entity} · <span className="text-muted-foreground">{a.user_name}</span></span>
                    <span className="text-xs text-muted-foreground">{formatDateTime(a.created_at)}</span>
                  </div>
                ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nama"><Input value={uform.name} onChange={(e) => setUform((f) => ({ ...f, name: e.target.value }))} data-testid="staff-name" /></Field>
            <Field label="Email"><Input value={uform.email} onChange={(e) => setUform((f) => ({ ...f, email: e.target.value }))} data-testid="staff-email" /></Field>
            <Field label="Password"><Input type="password" value={uform.password} onChange={(e) => setUform((f) => ({ ...f, password: e.target.value }))} data-testid="staff-password" /></Field>
            <Field label="Peran">
              <Select value={uform.role} onValueChange={(v) => setUform((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={uform.can_delete} onChange={(e) => setUform((f) => ({ ...f, can_delete: e.target.checked }))} />Izinkan hapus catatan keuangan</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUserOpen(false)}>Batal</Button><Button onClick={createUser} data-testid="staff-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetPwd(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset Password{resetTarget ? ` · ${resetTarget.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Field label="Password Baru" required hint="Minimal 6 karakter. Beritahu pengguna untuk segera menggantinya.">
              <Input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="Password baru" data-testid="reset-password-input" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setResetPwd(""); }}>Batal</Button>
            <Button onClick={doResetPassword} data-testid="reset-password-save">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{catForm.id ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <ImageUpload value={catForm.image} onChange={(b) => setCatForm((f) => ({ ...f, image: b }))} label="Ikon" testId="category-image-upload" />
            <Field label="Nama Kategori" required><Input value={catForm.name} onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))} data-testid="category-name-input" /></Field>
            <Field label="Deskripsi"><Textarea value={catForm.description} onChange={(e) => setCatForm((f) => ({ ...f, description: e.target.value }))} data-testid="category-description-input" /></Field>
            <label className="flex items-center gap-2 text-sm"><Switch checked={catForm.active} onCheckedChange={(v) => setCatForm((f) => ({ ...f, active: v }))} data-testid="category-form-active" />Aktif (tampil di katalog & form produk)</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCatOpen(false)}>Batal</Button><Button onClick={saveCategory} data-testid="category-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!catDelTarget} onOpenChange={(v) => { if (!v) setCatDelTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Hapus kategori?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{catDelTarget?.name}{catDelTarget?.product_count > 0 ? ` masih dipakai ${catDelTarget.product_count} produk sehingga akan dinonaktifkan.` : " akan dihapus permanen."}</p>
          <DialogFooter><Button variant="outline" onClick={() => setCatDelTarget(null)}>Batal</Button><Button variant="destructive" onClick={deleteCategory} data-testid="category-confirm-delete">Hapus</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
